import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";

const money = (v, c) =>
  `${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

/** Replays the client's own statement and shows that it still equals what they hold. */
export const BalanceCheck = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.get("/money/reconcile");
      setData(res);
    } catch (e) {
      console.error("Balance check failed:", e);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  if (!data) return null;
  const rows = data.currencies.filter((r) => r.balance !== 0 || r.moved !== 0);

  return (
    <Card className="p-4 shadow-premium" data-testid="wallet-balance-check">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`icon-chip ${data.matched ? "bg-emerald-500/12 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>
            {data.matched ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium">Balance check</div>
            <div className="text-xs text-muted-foreground" data-testid="balance-check-message">
              {data.message}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={data.matched ? "default" : "secondary"} className="text-[10px]"
            data-testid="balance-check-status">
            {data.matched ? "Reconciled" : `${data.drifting.join(", ")} off`}
          </Badge>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={load}
            disabled={busy} title="Run the check again" data-testid="balance-check-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground text-start">
              <th className="text-start font-medium pb-2">Currency</th>
              <th className="text-end font-medium pb-2">Opening</th>
              <th className="text-end font-medium pb-2">Net movement</th>
              <th className="text-end font-medium pb-2">Statement says</th>
              <th className="text-end font-medium pb-2">You hold</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.currency} className="border-t border-border/60"
                data-testid={`balance-check-row-${r.currency}`}>
                <td className="py-2 font-medium">{r.currency}</td>
                <td className="py-2 text-end numeric text-muted-foreground">{money(r.opening, "")}</td>
                <td className={`py-2 text-end numeric ${r.moved >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {r.moved >= 0 ? "+" : ""}{money(r.moved, "")}
                </td>
                <td className="py-2 text-end numeric">{money(r.expected, "")}</td>
                <td className={`py-2 text-end numeric font-semibold ${Math.abs(r.drift) > 0.01 ? "text-amber-600" : ""}`}>
                  {money(r.balance, "")}
                  {Math.abs(r.drift) > 0.01 && (
                    <span className="ms-1 text-[10px] font-normal">({r.drift > 0 ? "+" : ""}{r.drift})</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
        Every deposit, payout, trade and fee moves your balance and writes its statement line in the
        same step, so the two can never fall out of step.
      </div>
    </Card>
  );
};
